/**
 * Widget Presets
 *
 * Pre-built configurations for common use cases.
 * Users can apply a preset by passing `preset: 'preset-name'` in their config.
 *
 * @example
 * Peptalk.init({
 *   preset: 'inline-form',
 *   ui: { target: '#widget-target' },
 *   features: { chat: { fields: [...] } }
 * });
 */

import React from 'react';
import htm from 'htm';
import { Config } from '@/types';

// Bind htm to React.createElement for use in presets
const html = htm.bind(React.createElement);

/**
 * PepTalk branded inline form preset.
 * Includes custom components for a professional form layout with:
 * - Custom radio buttons with icons
 * - Grid-based questionnaire container
 * - Custom submit button styling
 * - Form group with icon support
 */
const peptalkFormPreset = {
  customComponents: {
    RadioButton: (props: {
      option: { label: string; value: string };
      isSelected: boolean;
      onClick: () => void;
    }) => {
      const { option, isSelected, onClick } = props;

      const optionIcons: Record<string, string> = {
        'In-Person':
          'https://cdn.prod.website-files.com/619b6721260e523eeeff5f17/67c7fd4481de37ab4a631b1c_mic1.svg',
        Virtual:
          'https://cdn.prod.website-files.com/619b6721260e523eeeff5f17/67c7fd441d5c8a8ab21ee7ee_virtual1.svg',
      };

      const icon = optionIcons[option.label];

      return html`
        <button
          className=${isSelected
            ? 'peptalk-radio-btn-selected'
            : 'peptalk-radio-btn'}
          style=${{
            padding: '15px',
            borderRadius: '8px',
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isSelected ? 'white' : 'rgba(255, 255, 255, 0.05)',
            border: isSelected ? '1px solid #ffffff' : '1px solid #FFFFFF33',
          }}
          onClick=${onClick}
        >
          <div
            style=${{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
            }}
          >
            ${icon &&
            html`<img
              src=${icon}
              alt=${option.label}
              style=${{
                transition: 'all .2s linear',
                ...(isSelected
                  ? {
                      filter:
                        'invert(45%) sepia(7%) saturate(5240%) hue-rotate(177deg) brightness(88%) contrast(88%)',
                    }
                  : {}),
              }}
            />`}
            <div
              className=${isSelected
                ? 'peptalk-radio-text-selected'
                : 'peptalk-radio-text'}
              style=${{
                fontWeight: '300',
                fontSize: '16px',
                fontFamily: 'Sofia Pro, sans-serif',
                lineHeight: '1.5',
                flexGrow: 1,
                textAlign: 'left',
                color: isSelected ? '#41444D' : 'rgba(255, 255, 255, 0.6)',
              }}
            >
              ${option.label}
            </div>
            <div
              className=${isSelected
                ? 'peptalk-radio-indicator-selected'
                : 'peptalk-radio-indicator'}
              style=${{
                width: '20px',
                height: '20px',
                display: 'block',
                marginRight: '10px',
                borderRadius: '50%',
                border: isSelected
                  ? '6px solid #3971C0'
                  : '1px solid #FFFFFF33',
              }}
            />
          </div>
        </button>
      `;
    },

    QuestionnaireContainer: (props: {
      children: React.ReactNode;
      isFormSubmitting?: boolean;
    }) => {
      // PepTalk inline form layout configuration - based on FIELD NAMES, not indexes
      // This ensures layout works regardless of field order
      const fullWidthFields = ['name', 'eventType', 'details'];
      const sectionLabels: Record<string, string> = {
        name: 'Your details:',
        eventType: 'Event details:',
      };

      // Scoped CSS for form element overrides that can't be done inline
      const scopedStyles = `
        .peptalk-widget-container .overflow-y-auto {
          overflow-y: initial;
        }
        .peptalk-inline-form-container .peptalk-form-element {
          padding: 15px 27px !important;
          font-size: 16px !important;
          font-weight: 300 !important;
          line-height: 1.5 !important;
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid #FFFFFF33 !important;
          color: white !important;
        }
        .peptalk-inline-form-container .peptalk-form-element:focus {
          border: 1px solid white !important;
          outline: none !important;
        }
        .peptalk-inline-form-container .peptalk-date {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .peptalk-inline-form-container .peptalk-date.has-value {
          color: #FFFFFF !important;
        }
        .peptalk-inline-form-container .peptalk-form-element::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .peptalk-inline-form-container .peptalk-textarea,
        .peptalk-inline-form-container textarea.peptalk-form-element {
          color: white !important;
        }
        .peptalk-inline-form-container .peptalk-form-group-with-icon .peptalk-form-element {
          padding: 15px 40px !important;
          height: 53px !important;
        }
        .peptalk-inline-form-container .peptalk-radio {
          flex-grow: 1;
          width: 100% !important;
          gap: 20px !important;
        }
        .peptalk-inline-form-container .peptalk-radio > * {
          flex-grow: 1;
        }
        .peptalk-inline-form-container .peptalk-radio button.peptalk-radio-btn,
        .peptalk-inline-form-container button.peptalk-radio-btn {
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid #FFFFFF33 !important;
        }
        .peptalk-inline-form-container .peptalk-radio button.peptalk-radio-btn-selected,
        .peptalk-inline-form-container button.peptalk-radio-btn-selected {
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          background: white !important;
          background-color: white !important;
          border: 1px solid #ffffff !important;
        }
        .peptalk-inline-form-container .peptalk-radio button.peptalk-radio-btn-selected:hover,
        .peptalk-inline-form-container button.peptalk-radio-btn-selected:hover,
        .peptalk-inline-form-container button.peptalk-radio-btn-selected:focus {
          background: white !important;
          background-color: white !important;
        }
        .peptalk-inline-form-container .peptalk-radio-text {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .peptalk-inline-form-container .peptalk-radio-text-selected {
          color: #41444D !important;
        }
        .peptalk-inline-form-container .peptalk-radio-indicator {
          border: 1px solid #FFFFFF33 !important;
          background: transparent !important;
        }
        .peptalk-inline-form-container .peptalk-radio-indicator-selected {
          border: 6px solid #3971C0 !important;
          background: white !important;
        }
        .peptalk-inline-form-container .peptalk-date-wrapper {
          width: 100%;
        }
        .peptalk-inline-form-container .peptalk-textarea.peptalk-textarea-default {
          border-radius: 10px;
        }
        .peptalk-inline-form-container .peptalk-phone-input {
          display: flex !important;
          gap: 8px !important;
          width: 100% !important;
        }
        .peptalk-inline-form-container .peptalk-phone-country-select {
          flex-shrink: 0 !important;
          width: fit-content !important;
          min-width: 100px !important;
          cursor: pointer !important;
          color: white !important;
          font-weight: 300 !important;
        }
        .peptalk-inline-form-container .peptalk-phone-country-select:focus {
          background: rgba(255, 255, 255, 0.2) !important;
        }
        .peptalk-inline-form-container .peptalk-phone-country-select option {
          background: #2d3748 !important;
          color: white !important;
        }
        .peptalk-inline-form-container .peptalk-phone-number-input {
          flex: 1 !important;
          min-width: 0 !important;
        }
        .peptalk-inline-form-container .peptalk-select {
          font-size: 16px !important;
          font-weight: 300 !important;
          color: white !important;
        }
        .peptalk-inline-form-container .peptalk-select:invalid {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .peptalk-inline-form-container .peptalk-select option {
          background: #2d3748 !important;
          color: white !important;
        }
        .peptalk-inline-form-container .peptalk-select option:first-child {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .peptalk-inline-form-container .peptalk-form-group-error {
          color: rgb(251 214 209) !important;
        }
        .peptalk-inline-form-container {
          text-align: left !important;
        }
        .peptalk-country-dropdown-portal .peptalk-country-dropdown__search {
          border: 1px solid #FFFFFF33 !important;
          background: rgba(255, 255, 255, 0.05) !important;
        }
        .peptalk-country-dropdown-portal .peptalk-country-dropdown__search:focus {
          border-color: white !important;
          box-shadow: none !important;
        }
        /* Disabled state for inline-form elements — explicit override since
           the scoped !important rules prevent the disabled-state mixin
           from visually graying out inputs inside .peptalk-inline-form-container */
        .peptalk-inline-form-container .peptalk-form-element:disabled {
          background: rgba(255, 255, 255, 0.03) !important;
          color: rgba(255, 255, 255, 0.3) !important;
          border-color: rgba(255, 255, 255, 0.1) !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }
        .peptalk-inline-form-container .peptalk-country-dropdown--disabled .peptalk-country-dropdown__trigger {
          background: rgba(255, 255, 255, 0.03) !important;
          color: rgba(255, 255, 255, 0.3) !important;
          border-color: rgba(255, 255, 255, 0.1) !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }
        @media (max-width: 767px) {
          .peptalk-inline-form-container .peptalk-section-label {
            font-size: 16px !important;
            line-height: 110% !important;
            text-align: center !important;
          }
          .peptalk-submit-button {
            margin: 0 auto !important;
            display: block !important;
          }
          .peptalk-inline-form-container .peptalk-phone-input {
            gap: 8px !important;
          }
          .peptalk-inline-form-container .peptalk-phone-country-select {
            min-width: 90px !important;
            font-size: 14px !important;
          }
          .peptalk-inline-form-container .peptalk-select {
            font-size: 14px !important;
          }
        }
      `;

      const gridContainerStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        width: '100%',
        backgroundColor: '#3971C0',
        opacity: props.isFormSubmitting ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
      };

      const labelStyle = {
        width: '100%',
        fontFamily: 'Sofia Pro, sans-serif',
        fontWeight: '400',
        fontSize: '20px',
        lineHeight: '110%',
        color: '#ffffff',
        marginTop: '0',
      };

      const wrappedChildren = React.Children.map(
        props.children,
        (child, index) => {
          // Get field name from React key (set as field.name in Questionnaire component)
          const fieldName = React.isValidElement(child)
            ? String(child.key)
            : null;

          const isFullWidth = fieldName
            ? fullWidthFields.includes(fieldName)
            : false;
          const label = fieldName ? sectionLabels[fieldName] : undefined;

          const wrapperStyle = {
            flex: isFullWidth ? '1 1 100%' : '1 1 100%', //before: 1 1 calc(50% - 10px)
            minWidth: isFullWidth ? '100%' : 'min(250px, 100%)',
          };

          return html`
          <${React.Fragment} key=${index}>
            ${label && html`<div className="peptalk-section-label" style=${labelStyle}>${label}</div>`}
            <div style=${wrapperStyle}>${child}</div>
          </${React.Fragment}>
        `;
        }
      );

      return html`
        <div
          className="fade-in-up peptalk-inline-form-container"
          style=${{ animationDelay: '0.1s' }}
        >
          <style>
            ${scopedStyles}
          </style>
          <div style=${gridContainerStyle}>${wrappedChildren}</div>
        </div>
      `;
    },

    QuestionnaireSubmitButton: (props: {
      children: React.ReactNode;
      onClick?: () => void;
    }) => {
      return html`
        <button
          className="peptalk-submit-button"
          style=${{
            borderRadius: '100px',
            backgroundColor: '#FFFFFF',
            fontFamily: 'Recoleta Alt',
            fontWeight: '500',
            fontSize: '20px',
            lineHeight: '27.2px',
            color: '#3971C0',
            margin: 'unset',
            width: 'max-content',
            padding: '16px 60px',
          }}
          onClick="${props.onClick}"
        >
          ${props.children}
        </button>
      `;
    },

    QuestionnaireSubmitIcon: () => {
      return html``;
    },

    QuestionnaireSubmitText: () => {
      return html`Submit`;
    },

    FormGroup: (props: {
      children: React.ReactNode;
      htmlFor?: string;
      index?: number;
      error?: string;
    }) => {
      const optionIcons: Record<string, string> = {
        'peptalk-date':
          'https://cdn.prod.website-files.com/619b6721260e523eeeff5f17/67c7fd4a625c01d90c770561_Calendar.svg',
        'peptalk-budget':
          'https://cdn.prod.website-files.com/619b6721260e523eeeff5f17/68624c24ee993c5e7823c82c_Vector%20(9).svg',
      };

      const icon = props.htmlFor ? optionIcons[props.htmlFor] : undefined;

      return html`
        <div
          className=${'fade-in-up' +
          (icon ? ' peptalk-form-group-with-icon' : '')}
          style=${{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            animationDelay: props.index ? props.index * 0.1 + 's' : '0s',
          }}
        >
          <div
            style=${{
              position: 'relative',
              width: '100%',
            }}
          >
            ${icon &&
            html`<img
              src=${icon}
              alt=${props.htmlFor}
              style=${{
                position: 'absolute',
                top: '50%',
                left: '15px',
                transform: 'translateY(-50%)',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />`}
            ${props.children}
          </div>
          ${props.error &&
          html`<div className="peptalk-form-group-error">${props.error}</div>`}
        </div>
      `;
    },

    PhoneInputContainer: (props: {
      children: React.ReactNode;
      className?: string;
    }) => {
      return html`
        <div className=${'peptalk-phone-input ' + (props.className || '')}>
          ${props.children}
        </div>
      `;
    },

    PhoneInputCountrySelect: (
      props: React.SelectHTMLAttributes<HTMLSelectElement>
    ) => {
      return html`
        <select
          className="peptalk-form-element peptalk-phone-country-select"
          value=${props.value}
          onChange=${props.onChange}
          disabled=${props.disabled}
          aria-label=${props['aria-label']}
          autocomplete="off"
        >
          ${props.children}
        </select>
      `;
    },

    PhoneInputNumberInput: (
      props: React.InputHTMLAttributes<HTMLInputElement>
    ) => {
      return html`
        <input
          type=${props.type}
          id=${props.id}
          name=${props.name}
          placeholder=${props.placeholder}
          required=${props.required}
          disabled=${props.disabled}
          value=${props.value}
          onChange=${props.onChange}
          onBlur=${props.onBlur}
          onFocus=${props.onFocus}
          className="peptalk-form-element peptalk-phone-number-input"
          aria-label=${props['aria-label']}
          aria-describedby=${props['aria-describedby']}
          autocomplete="off"
        />
      `;
    },
  },

  theme: {
    colors: {
      secondary: '#5077be',
      neutrals: {
        primary: {
          900: '#4770ba',
          '900-disabled': '#5077be',
          750: '#5d81c3',
          850: 'rgba(255, 255, 255, 0.05)',
        },
      },
      surface: 'transparent',
      text: {
        primary: 'white',
        secondary: 'rgba(255, 255, 255, 0.6)',
      },
      border: {
        focus: 'white',
        secondary: '#FFFFFF33',
      },
    },
    typography: {
      fontSize: {},
    },
    borderRadius: {
      max: '10px',
    },
  },

  // Default UI settings for this preset
  ui: {
    formOnly: true,
  },
};

// Deep partial type for nested objects
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Preset configuration type with deeply partial theme.
 */
export interface PresetConfig {
  customComponents?: Partial<Config.CustomComponents>;
  theme?: DeepPartial<Config.Theme>;
  ui?: Partial<Config.UI>;
}

/**
 * Available presets map.
 * Add new presets here as needed.
 */
export const PRESETS: Record<string, PresetConfig> = {
  'inline-form': peptalkFormPreset,
};

/**
 * Get a preset by name.
 * Returns undefined if preset doesn't exist.
 */
export function getPreset(name: string) {
  return PRESETS[name];
}

/**
 * List all available preset names.
 */
export function listPresets(): string[] {
  return Object.keys(PRESETS);
}
